
import { useState, useCallback } from 'react';
import { StatCardData } from '../types';
import { saveCardsToStorage } from '../utils/storage';
import { useToast } from '@/hooks/use-toast';

export const useCardReordering = (
  statCards: StatCardData[],
  setStatCards: React.Dispatch<React.SetStateAction<StatCardData[]>>
) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const { toast } = useToast();

  // Validate if a drag operation should be allowed between two cards
  const validateDragOperation = useCallback((draggedId: string, targetId: string): boolean => {
    if (!draggedId || !targetId) return false;
    
    // Only allow reordering between two stat cards (not with dashboard container)
    const isStatCardDragged = draggedId && draggedId.startsWith('stat-');
    const isStatCardTarget = targetId && targetId.startsWith('stat-');
    
    // Both must be stat cards and not the same card
    return isStatCardDragged && isStatCardTarget && draggedId !== targetId;
  }, []);

  const handleReorderStatCards = useCallback((draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    
    // Validate this is a proper stat card to stat card operation
    if (!validateDragOperation(draggedId, targetId)) {
      console.log(`Invalid drag operation: Dragged (${draggedId}) → Target (${targetId})`);
      
      // Use setTimeout to avoid React state updates during render
      setTimeout(() => {
        toast({
          title: "Invalid Operation",
          description: "You can only reorder stat cards with other stat cards",
          variant: "destructive"
        });
      }, 0);
      
      return;
    }
    
    console.log(`Reordering: Dragged (${draggedId}) → Target (${targetId})`);
    
    setStatCards(prevCards => {
      const draggedIndex = prevCards.findIndex(card => card.id === draggedId);
      const targetIndex = prevCards.findIndex(card => card.id === targetId);
      
      if (draggedIndex === -1 || targetIndex === -1) {
        console.error('Card not found:', draggedIndex === -1 ? draggedId : targetId);
        
        // Use setTimeout to avoid React state updates during render
        setTimeout(() => {
          toast({
            title: "Reordering Failed",
            description: "One of the cards could not be found",
            variant: "destructive"
          });
        }, 0);
        
        return prevCards;
      }
      
      // Create a new array to avoid mutating the original
      const newCards = [...prevCards];
      
      // Get the cards
      const draggedCard = newCards[draggedIndex];
      const targetCard = newCards[targetIndex];
      
      // Swap their orders
      const draggedOrder = draggedCard.order;
      draggedCard.order = targetCard.order;
      targetCard.order = draggedOrder;
      
      // Sort by order property
      newCards.sort((a, b) => a.order - b.order);
      
      // Show success toast
      setTimeout(() => {
        toast({
          title: "Cards Reordered",
          description: `"${draggedCard.title}" swapped with "${targetCard.title}"`,
        });
      }, 0);
      
      // Save to localStorage
      saveCardsToStorage(newCards);
      
      return newCards;
    });
  }, [toast, validateDragOperation]);

  return { handleReorderStatCards, validateDragOperation };
};
